import {
  type OrderRecord, type SingleAlgoFullReport, AlgoStatusString, OrderState,
  type QEPNotification,
  QEPNotificationActionType, QEPNotificationType, type QEPNotificationAction, type QEPNotificationPayload, QEPNotificationStatus,
  ComponentType, LinkerDataType,
  type QEPSystemAction,
} from '@qep/types/src';

function processOrder(
  notification: QEPNotification<QEPNotificationActionType>,
  notificationUpdate: QEPNotificationPayload<QEPNotificationActionType>,
  previousNotification?: QEPNotification<QEPNotificationActionType>,
): void {
  const order = notificationUpdate?.payload as OrderRecord;
  const isCriticalRejection: boolean = Number(order.msgType) === 9;

  if (order.status !== (previousNotification?.payload as OrderRecord)?.status) {
    notification.isRead = false;
  } else if (previousNotification && !isCriticalRejection) {
    notification.hasNotified = true;
    notification.isRead = previousNotification.isRead;
  }

  let orderStatus: number | string = Number(order.status);
  if (Number.isNaN(Number(order.status))) {
    orderStatus = order.status ?? '';
  }

  if (isCriticalRejection) {
    notification.status = QEPNotificationStatus.CRITICAL;
    return;
  }

  switch (orderStatus) {
    case OrderState.ORDERSTATE_REJECTED: {
      notification.status = QEPNotificationStatus.CRITICAL;
      break;
    }

    case OrderState.ORDERSTATE_CANCELED: {
      notification.status = QEPNotificationStatus.HIGH;
      break;
    }

    case OrderState.ORDERSTATE_CANCEL_REJECTED: {
      notification.status = QEPNotificationStatus.CRITICAL;
      break;
    }

    case OrderState.ORDERSTATE_FILLED: {
      notification.status = QEPNotificationStatus.MEDIUM;
      break;
    }

    case OrderState.ORDERSTATE_PARTIAL_FILLED: {
      notification.status = QEPNotificationStatus.MEDIUM;
      break;
    }

    default: {
      break;
    }
  }
}

export const processNotification = (
  notificationUpdate: QEPNotificationPayload<QEPNotificationActionType>,
  previousNotification?: QEPNotification<QEPNotificationActionType>,
): QEPNotification<QEPNotificationActionType> => {
  const notification: QEPNotification<QEPNotificationActionType> = {
    id: notificationUpdate.id,
    type: notificationUpdate.type,
    title: notificationUpdate.title,
    shortDescription: notificationUpdate.shortDescription ?? '',
    longDescription: notificationUpdate.longDescription,
    payload: notificationUpdate.payload,
    isRead: true,
    hasNotified: false,
    status: QEPNotificationStatus.LOW,
    timestamp: new Date().toISOString(),
  };
  switch (notificationUpdate.type) {
    case QEPNotificationType.ALERT: {
      break;
    }

    case QEPNotificationType.SYSTEM: {
      break;
    }

    case QEPNotificationType.ORDER: {
      processOrder(notification, notificationUpdate, previousNotification);
      break;
    }

    case QEPNotificationType.ALGO: {
      const algo = notificationUpdate?.payload as SingleAlgoFullReport;
      if (algo.algoStatus !== (previousNotification?.payload as SingleAlgoFullReport)?.algoStatus) {
        notification.isRead = false;
      } else if (previousNotification) {
        notification.hasNotified = true;
        notification.isRead = previousNotification.isRead;
      }

      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
      switch (algo.algoStatus as unknown as AlgoStatusString) {
        case AlgoStatusString.ALGOSTATUS_ABORTED_BY_SYSTEM: {
          notification.status = QEPNotificationStatus.CRITICAL;
          break;
        }

        case AlgoStatusString.ALGOSTATUS_ABORTED: {
          notification.status = QEPNotificationStatus.HIGH;
          break;
        }

        case AlgoStatusString.ALGOSTATUS_ERROR: {
          notification.status = QEPNotificationStatus.CRITICAL;
          break;
        }

        case AlgoStatusString.ALGOSTATUS_PAUSED: {
          notification.status = QEPNotificationStatus.MEDIUM;
          break;
        }

        case AlgoStatusString.ALGOSTATUS_INCOMPLETE_REACHED_END_TIME: {
          notification.status = QEPNotificationStatus.MEDIUM;
          break;
        }

        case AlgoStatusString.ALGOSTATUS_INCOMPLETE_MARKET_CLOSED: {
          notification.status = QEPNotificationStatus.MEDIUM;
          break;
        }

        default: {
          notification.status = QEPNotificationStatus.LOW;
          break;
        }
      }

      break;
    }
  }

  notification.actions = buildOrderActionPayload(notificationUpdate) ?? previousNotification?.actions ?? [];

  return notification;
};

function buildOrderActionPayload(notification: QEPNotificationPayload<QEPNotificationActionType>): Array<QEPNotificationAction<QEPNotificationActionType>> {
  if (notification?.payload?.algoRunId) {
    return buildAlgoActions(notification);
  }

  if ((notification?.payload as OrderRecord)?.orderId) {
    return buildOrderActions(notification);
  }

  return [];
}

const buildOrderActions = (notification: QEPNotificationPayload<QEPNotificationActionType>): Array<QEPNotificationAction<QEPNotificationActionType>> => {
  const order = notification?.payload as OrderRecord;
  const actionPayload: QEPNotificationAction<QEPNotificationActionType> = {
    type: QEPNotificationActionType.SHARE,
    label: 'Clone',
    data: {} as unknown as QEPSystemAction,
  };
  actionPayload.data = {
    componentType: ComponentType.ORDER_ENTRY,
    dataType: LinkerDataType.ORDER,
    data: order,
    meta: {},
  };
  return [actionPayload];
};

const buildAlgoActions = (notification: QEPNotificationPayload<QEPNotificationActionType>): Array<QEPNotificationAction<QEPNotificationActionType>> => {
  const actionPayload: QEPNotificationAction<QEPNotificationActionType> = {
    type: QEPNotificationActionType.SHARE,
    label: 'Clone',
    data: {} as unknown as QEPSystemAction,
  };
  actionPayload.data = {
    componentType: ComponentType.ORDER_ENTRY,
    dataType: LinkerDataType.ALGO,
    data: {
      algoRunId: notification.payload?.algoRunId,
    },
    meta: {},
  };
  return [actionPayload];
};
